import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';
import { CarouselItem, I18n, UserId, Uuid } from '../Base';
import Repository, { RepositoryElement, RepositoryJson } from '../Repository';
import { PartialRecord } from '../Utils';

enum SpaceMembershipTierState {
  Available = 'available',
  WaitingToDisable = 'waitingToDisable',
  Disabled = 'disabled',
}

type MonthlySubscriptionPlan<T = number> = {
  rewardOfferId: Uuid;
  price: number; // in cents
  newPrice?: number; // in cents
  willBeEffectiveAt?: T;
};

type MonthlySubscription<T = number> = {
  monthly: MonthlySubscriptionPlan<T>;
  quota: number;
  lastExpireAt?: T;
};

type PermanentSubscriptionPlan = {
  rewardOfferId: Uuid;
};

type PermanentSubscription = {
  permanent: PermanentSubscriptionPlan;
};

enum StatisticsKey {
  SubscriptionCount = 'subscriptionCount',
  MonthlySubscriptionCount = 'monthlySubscriptionCount',
  ActiveMonthlySubscriptionCount = 'activeMonthlySubscriptionCount',
  permanentSubscriptionCount = 'permanentSubscriptionCount',
  ActivePermanentSubscriptionCount = 'activePermanentSubscriptionCount',
}

type I18nClassroomMembershipTierInfo = I18n<{
  name: string;
  shortDescription: string | null;
  description: string | null;
  carousel: CarouselItem[];
}>;

export type SpaceMembershipTierJson<T = number> = {
  classroomId: Uuid;
  tierId: Uuid;
  createdBy: UserId;
  createdAt: T;
  lastModifiedAt: T;
  state: SpaceMembershipTierState;
  i18nClassroomMembershipTierInfo: I18nClassroomMembershipTierInfo;
  classroomCreditMultiplier: number; // double
  subscription?: MonthlySubscription<T> | PermanentSubscription;
  statistics: PartialRecord<StatisticsKey, number>;
};

export class SpaceMembershipTier
  extends RepositoryElement
  implements SpaceMembershipTierJson<Date>
{
  readonly tierId!: Uuid; //identifier
  readonly classroomId!: Uuid; //identifier
  createdBy!: UserId;
  createdAt!: Date;
  lastModifiedAt!: Date;
  state!: SpaceMembershipTierState;
  i18nClassroomMembershipTierInfo!: I18nClassroomMembershipTierInfo;
  classroomCreditMultiplier!: number;
  subscription!: PermanentSubscription | MonthlySubscription<Date>;
  statistics!: PartialRecord<StatisticsKey, number>;

  constructor(inventory: SpaceMembershipTierJson<Date | number>) {
    super();
    Object.assign(this, deepTransformTimestamp(inventory));
  }

  id(): string {
    return `${this.classroomId}_${this.tierId}`;
  }

  toJSON(): SpaceMembershipTierJson {
    return deepTransformDate(this) as unknown as SpaceMembershipTierJson;
  }
}

export enum SpaceMembershipTierMapType {
  ActiveUser = 'activeUser',
}
export type SpaceMembershipTierMapField = SpaceMembershipTierMapType;
export enum SpaceMembershipTierListType {
  All = 'all',
}
export type SpaceMembershipTierListField = string; // classroomId

export class SpaceMembershipTierRepository extends Repository<
  SpaceMembershipTier,
  SpaceMembershipTierJson<Date | number>,
  SpaceMembershipTierMapField,
  SpaceMembershipTierListField
> {
  constructor(
    repository?: RepositoryJson<
      SpaceMembershipTierJson<Date | number>,
      SpaceMembershipTierMapField,
      SpaceMembershipTierListField
    >,
  ) {
    super(
      {
        ...repository,
        mapFields: Repository.initFields(repository?.mapFields, {
          [SpaceMembershipTierMapType.ActiveUser]: {},
        }),
      },
      (x) => new SpaceMembershipTier(x),
    );
  }
}
